import React, { Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AccessibilityProvider } from './context/accessibility-context';
import { LocationProvider } from './context/location-context';
import { ConfirmProvider } from './context/confirm-context';
import { Routes, Route, useLocation } from 'react-router-dom';
import ScrollToTop from './components/common/ScrollToTop';
import usePageTracking from './hooks/usePageTracking';
import './assets/styles/App.css';
import './assets/styles/layout.css';
import './assets/styles/pages.css';
import './assets/styles/profile.css';
import './assets/styles/businesses.css';
import './assets/styles/events.css';
import './assets/styles/resources.css';
import './assets/styles/blog.css';
import './assets/styles/auth.css';
import './assets/styles/submission.css';
import './assets/styles/admin.css';
import './assets/styles/accessibility.css';
import './assets/styles/business-detail-page.css';
import './assets/styles/giveaway.css';

//3rd party middleware
import 'leaflet/dist/leaflet.css';

// Layout components (always needed - keep eagerly loaded)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Auth components (needed for route wrapping - keep eagerly loaded)
import ProtectedRoute from './components/auth/ProtectedRoute';

// Lazy-loaded page components
const HomePage = React.lazy(() => import('./pages/HomePage'));
const HomePageV2 = React.lazy(() => import('./pages/HomePageV2'));
const HomePageV3 = React.lazy(() => import('./pages/HomePageV3'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const BusinessesPage = React.lazy(() => import('./pages/BusinessesPage'));
const BusinessDetailPage = React.lazy(() => import('./pages/BusinessDetailPage'));
const EventsPage = React.lazy(() => import('./pages/EventsPage'));
const EventDetailPage = React.lazy(() => import('./pages/EventDetailPage'));
const ResourcesPage = React.lazy(() => import('./pages/ResourcesPage'));
const ResourceDetailPage = React.lazy(() => import('./pages/ResourceDetailPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const PartnersPage = React.lazy(() => import('./pages/PartnersPage'));
const PartnerDetailPage = React.lazy(() => import('./pages/PartnerDetailPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const CheckEmailPage = React.lazy(() => import('./pages/CheckEmailPage'));
const AuthCallbackPage = React.lazy(() => import('./pages/AuthCallbackPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SuggestListingPageNew = React.lazy(() => import('./pages/SuggestListingPageNew'));
const SubmitBusinessPage = React.lazy(() => import('./pages/SubmitBusinessPage'));
const SubmitResourcePage = React.lazy(() => import('./pages/SubmitResourcePage'));
const SubmitBlogPage = React.lazy(() => import('./pages/SubmitBlogPage'));
const SubmitEventPage = React.lazy(() => import('./pages/SubmitEventPage'));
const ManageEventPage = React.lazy(() => import('./pages/ManageEventPage'));
const ManageBlogPage = React.lazy(() => import('./pages/ManageBlogPage'));
const ManageBusinessPage = React.lazy(() => import('./pages/ManageBusinessPage'));
const ManageSubscriptionPage = React.lazy(() => import('./pages/ManageSubscriptionPage'));
const ManageResourcePage = React.lazy(() => import('./pages/ManageResourcePage'));
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const NewAdminDashboardPage = React.lazy(() => import('./pages/NewAdminDashboardPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const InvoiceHistoryPage = React.lazy(() => import('./pages/InvoiceHistoryPage'));
const SavedItemsPage = React.lazy(() => import('./pages/SavedItemsPage'));
const KindlyAlertedPage = React.lazy(() => import('./pages/KindlyAlertedPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const DeveloperNotesPage = React.lazy(() => import('./pages/DeveloperNotesPage'));
const HumanRightsAwardPage = React.lazy(() => import('./pages/HumanRightsAwardPage'));
const IHeartRadioInterviewPage = React.lazy(() => import('./pages/IHeartRadioInterviewPage'));
const PressPage = React.lazy(() => import('./pages/PressPage'));
const GiveawayPage = React.lazy(() => import('./pages/GiveawayPage'));
const UnsubscribePage = React.lazy(() => import('./pages/UnsubscribePage'));
const ClaimBusinessPage = React.lazy(() => import('./pages/ClaimBusinessPage'));
const ClaimWithTokenPage = React.lazy(() => import('./pages/ClaimWithTokenPage'));
const NewsletterSignupPage = React.lazy(() => import('./pages/NewsletterSignupPage'));

// KindlyMe (lazy-loaded)
const CreateKindlyMeCard = React.lazy(() => import('./pages/kindlyme/CreateKindlyMeCard'));
const KindlyMeDashboard = React.lazy(() => import('./pages/kindlyme/KindlyMeDashboard'));
const EditKindlyMeCard = React.lazy(() => import('./pages/kindlyme/EditKindlyMeCard'));
const ViewKindlyMeCard = React.lazy(() => import('./pages/kindlyme/ViewKindlyMeCard'));

// Guides (lazy-loaded)
const First90DaysGuidePage = React.lazy(() => import('./pages/guides/First90DaysGuidePage'));

// Tools (lazy-loaded)
const IEPMeetingPrepPage = React.lazy(() => import('./pages/tools/IEPMeetingPrepPage'));
const DoctorVisitPrepPage = React.lazy(() => import('./pages/tools/DoctorVisitPrepPage'));
const VisitPrepKitPage = React.lazy(() => import('./pages/tools/VisitPrepKitPage'));
const StoryCreatorPage = React.lazy(() => import('./pages/tools/StoryCreatorPage'));
const AccessibilityAssessmentPage = React.lazy(() => import('./pages/tools/AccessibilityAssessmentPage'));
const StaffTrainingPage = React.lazy(() => import('./pages/tools/StaffTrainingPage'));

function App() {
  const location = useLocation();

  // Initialize page tracking
  usePageTracking();

  // Check if current path is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Signal prerender ready for pages without async data
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!window.prerenderReady) {
        window.prerenderReady = true;
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Add global error handler to suppress Stripe-related errors
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = function(msg, ...args) {
      // Suppress specific Stripe-related errors
      if (
        (typeof msg === 'string' && msg.includes("Cannot find module './en'")) ||
        (args.length > 0 && args.some(arg =>
          typeof arg === 'string' &&
          (arg.includes('hcaptcha') || arg.includes('cs_test_'))
        ))
      ) {
        // Suppress these specific errors
        return;
      }
      // Pass through other errors to the original console.error
      return originalConsoleError.apply(console, [msg, ...args]);
    };

    // Cleanup function to restore original console.error
    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  return (
    <HelmetProvider>
      <AccessibilityProvider>
        <LocationProvider>
          <ConfirmProvider>
          <div className="app overflow-x-hidden">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#1C1E21',
                  padding: '16px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  fontSize: '14px',
                  maxWidth: '500px',
                },
                success: {
                  iconTheme: {
                    primary: '#4BB0A8',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            <ScrollToTop />
            {!isAdminRoute && <Header />}
          <main className={isAdminRoute ? "" : "main-content bg-gradient-to-r from-teal-50 to-purple-50 overflow-x-hidden -mt-[5px] -mb-[8px] print:bg-white print:m-0"}>
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="inline-block w-12 h-12 border-4 border-t-[#4BB0A8] border-r-[#4BB0A8] border-b-transparent border-l-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        }>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/v2" element={<HomePageV2 />} />
          <Route path="/v3" element={<HomePageV3 />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/businesses" element={<BusinessesPage />} />
          <Route path="/businesses/id/:id" element={<BusinessDetailPage />} />
          <Route path="/businesses/:slug" element={<BusinessDetailPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:identifier" element={<EventDetailPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:id" element={<ResourceDetailPage />} />
          <Route path="/resource/:slug" element={<ResourceDetailPage />} /> {/* Consider changing to /resources/:slug */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/post/:slug" element={<BlogPostPage />} /> {/* More specific route first */}
          <Route path="/blog/:id" element={<BlogPostPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/2025-human-rights-award" element={<HumanRightsAwardPage />} />
          <Route path="/iheartradio-interview" element={<IHeartRadioInterviewPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/partners/:slug" element={<PartnerDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/developer-notes" element={<DeveloperNotesPage />} />
          <Route path="/giveaway" element={<GiveawayPage />} />
          <Route path="/giveaway/:id" element={<GiveawayPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/check-email" element={<CheckEmailPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/newsletter" element={<NewsletterSignupPage />} />
          <Route path="/accessibility-card" element={<ViewKindlyMeCard />} />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/suggest-listing" element={<SuggestListingPageNew />} />
          <Route path="/claim/:token" element={<ClaimWithTokenPage />} />
          <Route path="/claim-business" element={<ClaimBusinessPage />} />
          <Route path="/submit-business" element={
            <ProtectedRoute>
              <SubmitBusinessPage />
            </ProtectedRoute>
          } />
          <Route path="/manage-business/:businessId" element={
            <ProtectedRoute>
              <ManageBusinessPage />
            </ProtectedRoute>
          } />
          <Route path="/manage-subscription/:businessId" element={
            <ProtectedRoute>
              <ManageSubscriptionPage />
            </ProtectedRoute>
          } />
          <Route path="/business/:businessId/invoices" element={
            <ProtectedRoute>
              <InvoiceHistoryPage />
            </ProtectedRoute>
          } />
          <Route path="/submit-resource" element={
            <ProtectedRoute>
              <SubmitResourcePage />
            </ProtectedRoute>
          } />
          <Route path="/submit-blog" element={
            <ProtectedRoute>
              <SubmitBlogPage />
            </ProtectedRoute>
          } />
          <Route path="/submit-event" element={
            <ProtectedRoute>
              <SubmitEventPage />
            </ProtectedRoute>
          } />
          <Route path="/manage-event/:eventId" element={
            <ProtectedRoute>
              <ManageEventPage />
            </ProtectedRoute>
          } />
          <Route path="/manage-blog/:blogId" element={
            <ProtectedRoute>
              <ManageBlogPage />
            </ProtectedRoute>
          } />
          <Route path="/manage-resource/:resourceId" element={
            <ProtectedRoute>
              <ManageResourcePage />
            </ProtectedRoute>
          } />
          <Route path="/saved-items" element={
            <ProtectedRoute>
              <SavedItemsPage />
            </ProtectedRoute>
          } />
          {/* New Modern Admin Dashboard */}
          <Route path="/admin/*" element={
            <ProtectedRoute requiredRole="Admin">
              <NewAdminDashboardPage />
            </ProtectedRoute>
          } />
          {/* Legacy Admin Dashboard (for reference) */}
          <Route path="/admin-legacy" element={
            <ProtectedRoute requiredRole="Admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/kindly-alerted" element={
            <ProtectedRoute>
              <KindlyAlertedPage />
            </ProtectedRoute>
          } />

          {/* KindlyMe - Public page (handles auth state internally) */}
          <Route path="/kindlyme" element={<KindlyMeDashboard />} />
          <Route path="/kindlyme/create" element={
            <ProtectedRoute>
              <CreateKindlyMeCard />
            </ProtectedRoute>
          } />
          <Route path="/kindlyme/edit/:id" element={
            <ProtectedRoute>
              <EditKindlyMeCard />
            </ProtectedRoute>
          } />

          {/* Guides Routes */}
          <Route path="/guides/first-90-days" element={<First90DaysGuidePage />} />

          {/* Tools Routes */}
          <Route path="/tools/iep-meeting-prep" element={<IEPMeetingPrepPage />} />
          <Route path="/tools/doctor-visit-prep" element={<DoctorVisitPrepPage />} />
          <Route path="/tools/visit-prep-kit" element={<VisitPrepKitPage />} />
          <Route path="/tools/story-creator" element={<StoryCreatorPage />} />
          <Route path="/tools/accessibility-assessment" element={<AccessibilityAssessmentPage />} />
          <Route path="/tools/staff-training" element={<StaffTrainingPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
          </main>
          {!isAdminRoute && <Footer />}
          </div>
          </ConfirmProvider>
        </LocationProvider>
      </AccessibilityProvider>
    </HelmetProvider>
  );
}

export default App;
